/**
 * roles.js — the single place that turns a role value into something a person
 * reads, and into where that person's work lives.
 *
 * This module exists because the mapping was duplicated in three components and
 * two of them were missing the family roles entirely. The result: a parent's
 * user chip displayed the raw database value "family_owner", and her breadcrumb
 * linked to /coordinator/clients. Both are the same bug — a lookup table with a
 * fallback that silently exposes internals when a case is missing.
 *
 * So: one table, and no raw-value fallback anywhere it's read.
 *
 * ON THE NAMES
 * The database stores family_owner / family_contributor / family_viewer. Those
 * are constants with migration cost and no user ever sees them. What a user sees
 * is below. "Owner" was the original label and it was wrong twice over: cold for
 * a product about a parent and their child, and it presumes the person is family
 * when they may be a conservator, a sibling, or a lifelong friend.
 *
 * "Organizer" says what they actually do — set the profile up, invite people,
 * decide what's published — in a word a grandparent reads without effort, and it
 * does not collide with the agency's "Coordinator".
 */

export const ROLE_LABEL = {
  agency_admin:       "Admin",
  coordinator:        "Coordinator",
  caregiver:          "DSP",
  family_owner:       "Organizer",
  contributor:        "Contributor",   // a guest in an agency account (Oct 24)
  family_contributor: "Contributor",
  family_viewer:      "Viewer",
};

/** Where this role's list of people lives. */
export const CLIENTS_PATH = {
  agency_admin:       "/agency",
  coordinator:        "/coordinator/clients",
  caregiver:          "/caregiver/clients",
  family_owner:       "/family",
  contributor:        "/contribute",
  family_contributor: "/contribute",
  family_viewer:      "/family",
};

/** What that list is called, in each audience's own words. */
export const CLIENTS_LABEL = {
  agency_admin:       "Your clients",
  coordinator:        "Your clients",
  caregiver:          "Your clients",
  family_owner:       "Your profiles",
  contributor:        "Your profiles",
  family_contributor: "Your profiles",
  family_viewer:      "Your profiles",
};

export const FAMILY_ROLES = ["family_owner", "family_contributor", "family_viewer"];

export function isFamilyRole(role) {
  return FAMILY_ROLES.includes(role);
}

/**
 * Display label for a role. Returns "" rather than the raw value when a role is
 * unknown — showing nothing is better than showing a database enum to a user.
 */
export function roleLabel(role) {
  return ROLE_LABEL[role] || "";
}

/**
 * A contributor on this client (Oct 24): may add, may not edit. Read from the
 * per-client `viewer_capabilities` the server puts on /clients/:id and
 * /clients/:id/profile — a capability answer, never a role name. Mirrors
 * permissions.contributes_without_editing. False while caps are unknown.
 */
export function contributesWithoutEditing(caps) {
  return Array.isArray(caps) && caps.includes("contribute_client") && !caps.includes("edit_client");
}

/**
 * Where a signed-in user belongs (Oct 24).
 *
 * The server says, on the session user (`landing`, from permissions.landing_for).
 * The fallback mirrors that table for a session payload that predates the
 * field. This replaced three hand-written copies — LoginPage and EntryFork had
 * no contributor case, so a returning contributor was sent to /caregiver/clients
 * (agency) or /family (family account).
 */
export function landingFor(user) {
  if (!user) return "/login";
  if (user.landing) return user.landing;
  if (user.role === "family_contributor" || user.role === "contributor") return "/contribute";
  if (user.account_type === "family") return "/family";
  if (user.role === "agency_admin")   return "/agency";
  if (user.role === "coordinator")    return "/coordinator/clients";
  return "/caregiver/clients";
}
